import { useEffect, useState } from "react";
import type { AuthConfig } from "@riea-water/contracts";

/** One automatic redirect per tab, so a failed or refused sign-in shows the card instead of looping. */
const AUTO_KEY = "riea-water.auto-sign-in";

/** Step 1 of the journey, as the prototype draws it (docs/UX.md section 1). */
export function SignIn(props: {
  config: AuthConfig;
  onSignIn: () => void;
  busy?: boolean;
  auto?: boolean;
}) {
  const { auto, onSignIn } = props;
  const [redirecting] = useState(
    () => auto === true && window.sessionStorage.getItem(AUTO_KEY) === null,
  );
  useEffect(() => {
    if (!redirecting) return;
    window.sessionStorage.setItem(AUTO_KEY, "1");
    onSignIn();
  }, [redirecting, onSignIn]);
  if (redirecting) return <Loading what="Signing you in" />;
  return (
    <div className="login">
      <div className="login-card">
        <Wordmark />
        <div>
          <div className="eyebrow">{props.config.tenantName} · Water Market workspace</div>
          <h1 style={{ marginTop: 6 }}>Sign in</h1>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          Use your {props.config.tenantName} account. Access is controlled by your directory groups.
        </p>
        <button
          className="btn primary block"
          type="button"
          onClick={props.onSignIn}
          disabled={props.busy}
        >
          Sign in with {props.config.tenantName} SSO
        </button>
        <div className="faint" style={{ fontSize: ".76rem" }}>
          UK-hosted · MFA required · sessions expire after 30 minutes idle
        </div>
        {props.config.demo ? (
          <div className="notice">Concept preview · fictional utility · synthetic data</div>
        ) : null}
      </div>
    </div>
  );
}

/** Step 2 in mock mode: the number-match prompt. With a real provider this page is the provider's. */
export function Mfa(props: {
  email: string;
  number: string;
  demo: boolean;
  onApprove: () => void;
  onBack: () => void;
}) {
  return (
    <div className="login">
      <div className="login-card">
        <Wordmark />
        <div>
          <div className="eyebrow">Multi-factor authentication</div>
          <h1 style={{ marginTop: 6 }}>Approve on your authenticator</h1>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          Enter this number in your authenticator app to approve the sign-in for{" "}
          <b>{props.email}</b>.
        </p>
        <div className="code" aria-label={`Number ${props.number}`}>
          {props.number.split("").map((digit, i) => (
            <span key={i}>{digit}</span>
          ))}
        </div>
        <button className="btn primary block" type="button" onClick={props.onApprove}>
          I have approved it
        </button>
        <button className="btn ghost block" type="button" onClick={props.onBack}>
          Use a different account
        </button>
        {props.demo ? (
          <div className="notice">Concept preview · fictional utility · synthetic data</div>
        ) : null}
      </div>
    </div>
  );
}

/** A user whose groups map to no role (ADM-06). */
export function Refused(props: { tenantName: string; detail: string; onBack: () => void }) {
  return (
    <div className="login">
      <div className="login-card">
        <Wordmark />
        <div>
          <div className="eyebrow">{props.tenantName} · Water Market workspace</div>
          <h1 style={{ marginTop: 6 }}>No access to this workspace</h1>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          {props.detail}
        </p>
        <button className="btn block" type="button" onClick={props.onBack}>
          Use a different account
        </button>
      </div>
    </div>
  );
}

export function Loading(props: { what: string }) {
  return (
    <div className="login">
      <div className="login-card" aria-busy="true">
        <Wordmark />
        <p className="muted" style={{ margin: 0 }}>
          {props.what}
        </p>
        <div className="skeleton" />
        <div className="skeleton" style={{ width: "60%" }} />
      </div>
    </div>
  );
}

function Wordmark() {
  return (
    <div className="wordmark">
      <span className="mark">
        <i />
      </span>
      RIEA Pro
    </div>
  );
}
